import { useState } from "react";
import {
  Plus,
  MessageSquare,
  Archive,
  Trash2,
  Pencil,
  ArchiveRestore,
  Search,
  X,
} from "lucide-react";
import { theme } from "../../../theme";

type ThreadItem = {
  id: string;
  title?: string;
  status: "regular" | "archived";
};

type ThreadSidebarProps = {
  threads: ThreadItem[];
  archivedThreads: ThreadItem[];
  activeThreadId: string | null;
  isLoading: boolean;
  onNewThread: () => void;
  onSelectThread: (threadId: string) => void;
  onRename: (threadId: string, newTitle: string) => void;
  onArchive: (threadId: string) => void;
  onUnarchive: (threadId: string) => void;
  onDelete: (threadId: string) => void;
  isOpen: boolean;
  onClose: () => void;
};

export const ThreadSidebar = ({
  threads,
  archivedThreads,
  activeThreadId,
  isLoading,
  onNewThread,
  onSelectThread,
  onRename,
  onArchive,
  onUnarchive,
  onDelete,
  isOpen,
  onClose,
}: ThreadSidebarProps) => {
  const [tab, setTab] = useState<"active" | "archived">("active");
  const [searchQuery, setSearchQuery] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");

  const displayThreads = tab === "active" ? threads : archivedThreads;
  const filteredThreads = searchQuery
    ? displayThreads.filter((t) =>
        (t.title ?? "New conversation").toLowerCase().includes(searchQuery.toLowerCase())
      )
    : displayThreads;

  const handleRenameSubmit = (threadId: string) => {
    if (editTitle.trim()) {
      onRename(threadId, editTitle.trim());
    }
    setEditingId(null);
    setEditTitle("");
  };

  const startEditing = (threadId: string, currentTitle: string) => {
    setEditingId(threadId);
    setEditTitle(currentTitle);
  };

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed md:relative inset-y-0 left-0 z-50 md:z-0
          w-[280px] flex-shrink-0 flex flex-col
          dark:bg-sy-navy bg-white border-r ${theme.border.base}
          transition-transform duration-200 ease-in-out
          ${isOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b dark:border-white/[0.06] border-gray-200">
          <h2 className={`text-sm font-semibold ${theme.text.heading}`}>Conversations</h2>
          <div className="flex items-center gap-1">
            <button
              onClick={onNewThread}
              aria-label="New conversation"
              className={`p-2 rounded-lg ${theme.text.accent} dark:hover:bg-white/[0.06] hover:bg-gray-100 transition-colors`}
            >
              <Plus className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              aria-label="Close sidebar"
              className={`p-2 rounded-lg md:hidden ${theme.text.muted} dark:hover:bg-white/[0.06] hover:bg-gray-100 transition-colors`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="px-3 py-2">
          <div className="relative">
            <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${theme.text.muted}`} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search conversations..."
              className={`w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border dark:border-white/[0.08] border-gray-200 dark:bg-white/[0.04] bg-gray-50 ${theme.text.body} placeholder:${theme.text.muted} focus:outline-none focus:ring-1 dark:focus:ring-sy-teal/30 focus:ring-teal-500/30`}
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex px-3 gap-1">
          <button
            onClick={() => setTab("active")}
            className={`flex-1 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              tab === "active"
                ? "dark:bg-sy-teal/[0.12] dark:text-sy-teal bg-teal-50 text-teal-700"
                : `${theme.text.muted} dark:hover:bg-white/[0.04] hover:bg-gray-50`
            }`}
          >
            Active ({threads.length})
          </button>
          <button
            onClick={() => setTab("archived")}
            className={`flex-1 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              tab === "archived"
                ? "dark:bg-amber-500/[0.12] dark:text-amber-400 bg-amber-50 text-amber-700"
                : `${theme.text.muted} dark:hover:bg-white/[0.04] hover:bg-gray-50`
            }`}
          >
            Archived ({archivedThreads.length})
          </button>
        </div>

        {/* Thread list */}
        <div className="flex-1 overflow-y-auto px-2 py-2 space-y-0.5">
          {isLoading ? (
            <div className={`${theme.status.loading} mt-4`}>
              <div className={`w-4 h-4 border-2 dark:border-sy-teal border-teal-500 border-t-transparent rounded-full ${theme.anim.spin}`} />
              <span className={`text-xs ${theme.text.muted}`}>Loading...</span>
            </div>
          ) : filteredThreads.length === 0 ? (
            <div className="flex flex-col items-center py-8 px-4">
              <MessageSquare className={`w-8 h-8 ${theme.text.muted} mb-2`} />
              <p className={`text-xs ${theme.text.muted} text-center`}>
                {searchQuery
                  ? "No matching conversations"
                  : tab === "active"
                    ? "No conversations yet"
                    : "No archived conversations"}
              </p>
            </div>
          ) : (
            filteredThreads.map((thread) => (
              <div
                key={thread.id}
                className={`group flex items-center gap-2 px-3 py-2.5 rounded-xl cursor-pointer transition-all duration-150 min-h-[44px] ${
                  activeThreadId === thread.id
                    ? "dark:bg-sy-teal/[0.10] bg-teal-50 dark:border-sy-teal/[0.20] border border-teal-100"
                    : "dark:hover:bg-white/[0.04] hover:bg-gray-50 border border-transparent"
                }`}
                onClick={() => onSelectThread(thread.id)}
              >
                <MessageSquare
                  className={`w-4 h-4 flex-shrink-0 ${
                    activeThreadId === thread.id ? theme.text.accent : theme.text.muted
                  }`}
                />

                {editingId === thread.id ? (
                  <input
                    autoFocus
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    onBlur={() => handleRenameSubmit(thread.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleRenameSubmit(thread.id);
                      if (e.key === "Escape") {
                        setEditingId(null);
                        setEditTitle("");
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className={`flex-1 text-xs bg-transparent border-b dark:border-sy-teal border-teal-500 ${theme.text.heading} focus:outline-none`}
                  />
                ) : (
                  <span
                    className={`flex-1 text-xs truncate ${
                      activeThreadId === thread.id ? theme.text.heading : theme.text.body
                    }`}
                  >
                    {thread.title || "New conversation"}
                  </span>
                )}

                {/* Action buttons (visible on hover) */}
                <div className="hidden group-hover:flex items-center gap-0.5 flex-shrink-0">
                  {tab === "active" ? (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          startEditing(thread.id, thread.title || "New conversation");
                        }}
                        aria-label="Rename"
                        className={`p-1 rounded ${theme.text.muted} hover:dark:text-white hover:text-gray-900 transition-colors`}
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onArchive(thread.id);
                        }}
                        aria-label="Archive"
                        className={`p-1 rounded ${theme.text.muted} hover:text-amber-500 transition-colors`}
                      >
                        <Archive className="w-3 h-3" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(thread.id);
                        }}
                        aria-label="Delete"
                        className={`p-1 rounded ${theme.text.muted} hover:text-red-500 transition-colors`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onUnarchive(thread.id);
                        }}
                        aria-label="Unarchive"
                        className={`p-1 rounded ${theme.text.muted} hover:dark:text-white hover:text-gray-900 transition-colors`}
                      >
                        <ArchiveRestore className="w-3 h-3" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(thread.id);
                        }}
                        aria-label="Delete"
                        className={`p-1 rounded ${theme.text.muted} hover:text-red-500 transition-colors`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};
